// The registry picker and the bulk importer, as the mockup's one modal (artboard 3, signed off
// 2026-08-28). R7: the search tab reads the MedQuery REGISTRY, not the staging table — staging IS the
// wheels, so sourcing from it could only ever offer variants somebody had already used. Adding is
// still a CREATE: POST /api/panels/{id}/snps writes a new staging row and leaves every other wheel's
// row alone. The Membership column is what makes that visible, now as an annotation.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Button, Input, Modal, Tabs } from "antd";
import { CheckOutlined, LockOutlined, PlusOutlined, UploadOutlined, WarningOutlined } from "@ant-design/icons";

import DataTable from "../../../../components/shared/DataTable";
import { useNotifications } from "../../../../context/NotificationContext";
import { attachPanelSnps } from "../../../../api/geneCodes/PanelApi";
import { bulkImportStaging, fetchStagingRecords } from "../../../../api/geneCodes/StagingApi";
import { searchRegistryVariants } from "../../../../api/geneCodes/RegistryApi";
import { createTraitGene } from "../../../../api/geneCodes/WheelGeneApi";
import { Panel } from "../../../../types/geneCodes/Panel";
import { Trait } from "../../../../types/geneCodes/TraitTypes";
import { BulkImportResult, Staging } from "../../../../types/geneCodes/StagingTypes";
import { RegistryGene, RegistryVariant } from "../../../../types/geneCodes/RegistryTypes";
import { panelStatusOf } from "../../../../utils/geneCodes/wheelAuthoring";
import GeneSymbolField from "./GeneSymbolField";
import { SnpCandidate, buildCandidates, registryRiskAllele, toAttachPayload, toImportRecords } from "./snpCatalogue";
import "./wheelBuilder.css";

// The mockup's modal.
const MODAL_WIDTH = 1040;
const PAGE_SIZE = 25;
// Two callers now hit /registry/* per keystroke (this and the gene autocomplete), so both debounce.
const SEARCH_DEBOUNCE_MS = 300;
// One staging read per search, for the membership annotation only — never the row source.
const MEMBERSHIP_LOOKUP_SIZE = 100;
export const PICKER_TASK = "pick:snp";

interface WheelSnpPickerProps {
	task: string;
	panel: Panel | null;
	trait: Trait | null;
	gene: string | null;
	panelSnps: Staging[];
	onClose: () => void;
	onAttached: () => void;
}

interface Filters {
	search: string;
	gene: string;
}

type RegistryState = "idle" | "ok" | "unavailable";

const errorsFrom = (error: unknown): string[] => {
	if (axios.isAxiosError(error) && error.response) {
		const data = error.response.data;
		if (data?.errors) return Object.values(data.errors).flat() as string[];
		if (data?.message) return [data.message];
	}
	if (error instanceof Error && error.message) return [error.message];
	return ["An unknown error occurred."];
};

// The registry evidence the doc already carries — measurability first, then how much is behind it.
const EvidenceCell: React.FC<{ doc: RegistryVariant }> = ({ doc }) => (
	<span className="mg-snp-picker__evidence" data-testid="snp-picker-evidence" data-state="found">
		{doc.on_array === false ? (
			<span className="mg-snp-picker__on-array mg-snp-picker__on-array--off" data-testid="snp-picker-off-array">
				<WarningOutlined /> Off-array
			</span>
		) : doc.on_array === true ? (
			<span className="mg-snp-picker__on-array mg-snp-picker__on-array--on">
				<CheckOutlined /> On array
			</span>
		) : (
			<span className="mg-snp-picker__on-array mg-snp-picker__on-array--unknown">Array unknown</span>
		)}
		<span className="mg-snp-picker__evidence-counts">
			{doc.assoc_count ?? 0} assoc · {doc.pmid_count ?? 0} papers
			{doc.best_p_value_text ? ` · p ${doc.best_p_value_text}` : ""}
		</span>
		{registryRiskAllele(doc) === null ? (
			<span className="mg-snp-picker__on-array mg-snp-picker__on-array--unknown" data-testid="snp-picker-no-risk-allele">
				No risk allele recorded — will not score
			</span>
		) : null}
	</span>
);

const WheelSnpPicker: React.FC<WheelSnpPickerProps> = ({ task, panel, trait, gene, panelSnps, onClose, onAttached }) => {
	const { showSuccess, showError } = useNotifications();

	const open = task === PICKER_TASK && Boolean(panel);
	const targetTrait = trait?.trait_name ?? null;
	// R6 put "Add gene" on the trait node, so the trait-level picker offers genes as well as variants.
	const canPickGenes = Boolean(trait) && !gene;

	const [tab, setTab] = useState("search");
	const [filters, setFilters] = useState<Filters>({ search: "", gene: "" });
	const [applied, setApplied] = useState<Filters>({ search: "", gene: "" });
	const [page, setPage] = useState(1);
	const [rows, setRows] = useState<RegistryVariant[]>([]);
	const [catalogueRows, setCatalogueRows] = useState<Staging[]>([]);
	const [registryState, setRegistryState] = useState<RegistryState>("idle");
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [selected, setSelected] = useState<SnpCandidate[]>([]);
	const [submitting, setSubmitting] = useState(false);
	const [errorMessages, setErrorMessages] = useState<string[]>([]);

	const [geneSymbol, setGeneSymbol] = useState("");
	const [pickedGene, setPickedGene] = useState<RegistryGene | null>(null);

	const [fileName, setFileName] = useState("");
	const [records, setRecords] = useState<Partial<Staging>[]>([]);
	const [importing, setImporting] = useState(false);
	const [importResult, setImportResult] = useState<BulkImportResult | null>(null);

	useEffect(() => {
		if (!open) return;
		setTab("search");
		setFilters({ search: "", gene: gene ?? "" });
		setApplied({ search: "", gene: gene ?? "" });
		setPage(1);
		setSelected([]);
		setErrorMessages([]);
		setGeneSymbol("");
		setPickedGene(null);
		setFileName("");
		setRecords([]);
		setImportResult(null);
	}, [open, gene]);

	useEffect(() => {
		const timer = setTimeout(() => setApplied(filters), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [filters]);

	const loadPage = useCallback(async () => {
		const query = applied.search.trim();
		const geneFilter = applied.gene.trim();

		if (query === "") {
			setRows([]);
			setCatalogueRows([]);
			setTotal(0);
			setRegistryState("idle");
			return;
		}

		setLoading(true);
		const outcome = await searchRegistryVariants({ query, ...(geneFilter ? { gene: geneFilter } : {}), page, limit: PAGE_SIZE });

		if (outcome.status !== "ok") {
			// FAIL CLOSED: an outage renders as an outage. An empty table would read as "no such variant".
			setRows([]);
			setCatalogueRows([]);
			setTotal(0);
			setRegistryState("unavailable");
			setLoading(false);
			return;
		}

		setRows(outcome.results);
		setTotal(outcome.total);
		setRegistryState("ok");

		// Membership is an ANNOTATION: a failed catalogue read must not turn a good registry page into an outage.
		try {
			// A hidden row is still on its wheel, so membership must see it.
			const catalogue = await fetchStagingRecords(1, MEMBERSHIP_LOOKUP_SIZE, "rs", "asc", { search: query, ...(geneFilter ? { gene: geneFilter } : {}) }, true);
			setCatalogueRows(catalogue.data ?? []);
		} catch {
			setCatalogueRows([]);
		}

		setLoading(false);
	}, [applied, page]);

	useEffect(() => {
		if (!open) return;
		void loadPage();
	}, [open, loadPage]);

	const candidates = useMemo(() => buildCandidates(rows, panel?.name_panel ?? null, panelSnps, targetTrait, catalogueRows), [rows, panel, panelSnps, targetTrait, catalogueRows]);

	const alreadyHereCount = candidates.filter((candidate) => candidate.alreadyHere).length;
	const unscoredCount = selected.filter((candidate) => registryRiskAllele(candidate.doc) === null).length;

	const applyFilter = (key: keyof Filters, value: string) => {
		setPage(1);
		setSelected([]);
		setFilters((previous) => ({ ...previous, [key]: value }));
	};

	const handleAttach = async () => {
		if (!panel || selected.length === 0) return;

		setSubmitting(true);
		setErrorMessages([]);
		try {
			const result = await attachPanelSnps(
				panel.id_panels,
				selected.map((candidate) => toAttachPayload(candidate.doc, targetTrait)),
			);
			// The server is the authority on what already existed, so its counts are what the toast says.
			const detail = result.skipped_count > 0 ? `${result.skipped_count} already in this wheel and skipped.` : undefined;
			showSuccess(`Added ${result.created_count} ${result.created_count === 1 ? "SNP" : "SNPs"} to ${panel.name_panel}.`, detail);
			onAttached();
			onClose();
		} catch (error) {
			setErrorMessages(errorsFrom(error));
			showError("Failed to add SNPs to this wheel.");
		} finally {
			setSubmitting(false);
		}
	};

	const handleAddGene = async () => {
		const symbol = geneSymbol.trim();
		if (!trait || symbol === "") return;

		setSubmitting(true);
		setErrorMessages([]);
		try {
			// The HGNC id rides along only when the author actually picked that option — never off stale state.
			const registryRef = pickedGene && pickedGene.symbol === symbol ? pickedGene.id : null;
			const { gene: created, created: isNew } = await createTraitGene(trait.idtraits, { gene_symbol: symbol, ...(registryRef ? { registry_ref: registryRef } : {}) });
			if (isNew) showSuccess(`Added ${created.symbol} to ${trait.trait_name}.`);
			else showSuccess(`${created.symbol} is already in ${trait.trait_name}.`, "Nothing was added.");
			onAttached();
			onClose();
		} catch (error) {
			setErrorMessages(errorsFrom(error));
			showError("Failed to add the gene to this trait.");
		} finally {
			setSubmitting(false);
		}
	};

	const handleFile = async (file: File) => {
		setErrorMessages([]);
		setImportResult(null);
		setFileName(file.name);
		try {
			const parsed = toImportRecords(await file.text(), { category: panel?.name_panel ?? "", subcategory: targetTrait });
			setRecords(parsed);
			if (parsed.length === 0) setErrorMessages(["No rows found. The file needs a header row with an `rs` column."]);
		} catch (error) {
			setRecords([]);
			setErrorMessages(errorsFrom(error));
		}
	};

	const handleImport = async () => {
		if (records.length === 0) return;

		setImporting(true);
		setErrorMessages([]);
		try {
			// A partial import is a 200: the endpoint commits the good rows and reports the rest in `errors`.
			const result = await bulkImportStaging(records);
			setImportResult(result);
			onAttached();
			if (result.errors.length > 0) {
				showError(`Imported ${result.created + result.updated} of ${records.length} rows.`, `${result.errors.length} could not be imported.`);
			} else {
				showSuccess(`Imported ${records.length} ${records.length === 1 ? "row" : "rows"}.`, `${result.created} created, ${result.updated} updated.`);
			}
		} catch (error) {
			setErrorMessages(errorsFrom(error));
			showError("Bulk import failed.");
		} finally {
			setImporting(false);
		}
	};

	if (!open || !panel) return null;

	const columns = [
		{
			title: "RS ID",
			key: "rs",
			render: (_: unknown, candidate: SnpCandidate) => <span className="mg-snp-picker__rs">{candidate.rs}</span>,
		},
		{
			title: "Gene",
			key: "gene",
			render: (_: unknown, candidate: SnpCandidate) => <span className="mg-snp-picker__gene">{(candidate.doc.gene_symbols ?? []).join(", ") || "—"}</span>,
		},
		{ title: "Registry evidence", key: "evidence", render: (_: unknown, candidate: SnpCandidate) => <EvidenceCell doc={candidate.doc} /> },
		{
			title: "Membership",
			key: "membership",
			render: (_: unknown, candidate: SnpCandidate) =>
				candidate.alreadyHere ? (
					<span className="mg-snp-picker__membership mg-snp-picker__membership--here" data-testid="snp-picker-membership">
						<CheckOutlined /> Already in {panel.name_panel}
					</span>
				) : (
					<span className={`mg-snp-picker__membership${candidate.memberships.length === 0 ? " mg-snp-picker__membership--none" : ""}`} data-testid="snp-picker-membership">
						{candidate.memberships.length === 0 ? "—" : `In ${candidate.memberships.join(", ")}`}
					</span>
				),
		},
	];

	const searchTab = (
		<div className="mg-snp-picker__tab">
			<div className="mg-snp-picker__filters">
				<Input allowClear placeholder="Search the registry — rsID, gene or trait" value={filters.search} onChange={(event) => applyFilter("search", event.target.value)} aria-label="Search registry" />
				<Input allowClear placeholder="Gene" value={filters.gene} onChange={(event) => applyFilter("gene", event.target.value)} aria-label="Gene" />
			</div>

			{registryState === "unavailable" ? (
				<div className="mg-snp-picker__note" data-testid="snp-picker-registry-state" data-state="unavailable">
					<span className="mg-snp-picker__evidence mg-snp-picker__evidence--unavailable">
						<LockOutlined /> Registry unavailable
					</span>
					<span>The registry could not be searched, so nothing has been ruled out. This is an outage, not an empty result — try again shortly.</span>
				</div>
			) : (
				<DataTable<SnpCandidate>
					bare
					dataSource={candidates}
					columns={columns}
					rowKey="rs"
					loading={loading}
					selectedRows={selected}
					setSelectedRows={setSelected}
					getCheckboxProps={(candidate) => ({ disabled: candidate.alreadyHere })}
					rowClassName={(candidate) => (candidate.alreadyHere ? "mg-snp-picker__row--here" : "")}
					total={total}
					currentPage={page}
					pageSize={PAGE_SIZE}
					onPageChange={(next) => {
						setSelected([]);
						setPage(next);
					}}
					minHeight={240}
					scrollY={320}
					emptyTitle={registryState === "idle" ? "Search the registry" : "No registry variants match this search"}
					emptyDescription={
						registryState === "idle"
							? "Type an rsID, gene or trait above. The picker searches the whole variant registry, not only the SNPs already used in a wheel."
							: "Widen the search, or add a SNP by hand from the inspector."
					}
					data-testid="snp-picker-table"
				/>
			)}

			<div className="mg-snp-picker__note" data-testid="snp-picker-note">
				<CheckOutlined />
				<span>
					Adding creates <strong>new rows</strong> in {panel.name_panel}. A SNP already in another wheel stays exactly where it is — a SNP can belong to several wheels, and nothing is moved out of another one.
				</span>
			</div>
		</div>
	);

	const geneTab = (
		<div className="mg-snp-picker__tab">
			<div className="mg-snp-picker__filters">
				<GeneSymbolField value={geneSymbol} onChange={setGeneSymbol} onPickGene={setPickedGene} placeholder="Gene symbol — search the registry" testId="snp-picker-gene-field" />
			</div>

			<div className="mg-snp-picker__note" data-testid="snp-picker-gene-note">
				<CheckOutlined />
				<span>
					The gene node is added to {targetTrait} with no SNPs. A symbol already on this trait is returned as-is — nothing is duplicated, and free text the registry does not know is still saved as typed.
				</span>
			</div>
		</div>
	);

	const importTab = (
		<div className="mg-snp-picker__tab">
			<div className="mg-snp-picker__upload">
				<label className="mg-snp-picker__file" htmlFor="snp-picker-file">
					<UploadOutlined />
					<span>{fileName || "Choose a CSV or TSV file"}</span>
				</label>
				<input
					id="snp-picker-file"
					type="file"
					accept=".csv,.tsv,.txt"
					className="mg-snp-picker__file-input"
					data-testid="snp-picker-file"
					onChange={(event) => {
						const file = event.target.files?.[0];
						if (file) handleFile(file);
					}}
				/>
				<span className="mg-snp-picker__hint">
					A header row plus <code>rs</code> and <code>risk_allele</code>. Rows without their own <code>category</code>/<code>subcategory</code> land in {panel.name_panel}
					{targetTrait ? ` › ${targetTrait}` : ""}.
				</span>
			</div>

			{records.length > 0 && (
				<div className="mg-snp-picker__parsed" data-testid="snp-picker-parsed">
					{records.length} {records.length === 1 ? "row" : "rows"} read from {fileName}.
				</div>
			)}

			{importResult && (
				<div className="mg-snp-picker__result" data-testid="snp-picker-result">
					<div className="mg-snp-picker__counts">
						<span data-testid="snp-picker-created">{importResult.created} created</span>
						<span data-testid="snp-picker-updated">{importResult.updated} updated</span>
						<span data-testid="snp-picker-failed">{importResult.errors.length} failed</span>
					</div>
					{importResult.errors.length > 0 && (
						<ul className="mg-snp-picker__errors">
							{importResult.errors.map((row) => (
								<li key={`${row.index}-${row.rs}`} data-testid="snp-picker-error-row">
									Row {row.index + 1} · {row.rs} — {row.error}
								</li>
							))}
						</ul>
					)}
				</div>
			)}
		</div>
	);

	const searchFooter = (
		<div className="mg-snp-picker__footer">
			<span className="mg-snp-picker__footer-hint" data-testid="snp-picker-footer-hint">
				<strong>{selected.length} selected</strong>
				{alreadyHereCount > 0 ? ` · ${alreadyHereCount} already in this wheel, skipped` : ""}
				{unscoredCount > 0 ? ` · ${unscoredCount} with no risk allele, will not score` : ""}
			</span>
			<div className="mg-snp-picker__footer-actions">
				<Button onClick={onClose} data-testid="snp-picker-cancel">
					Cancel
				</Button>
				<Button type="primary" icon={<PlusOutlined />} disabled={selected.length === 0} loading={submitting} onClick={handleAttach} data-testid="snp-picker-attach">
					Add {selected.length} {selected.length === 1 ? "SNP" : "SNPs"}
				</Button>
			</div>
		</div>
	);

	const geneFooter = (
		<div className="mg-snp-picker__footer">
			<span className="mg-snp-picker__footer-hint">A gene node holds SNPs; it can also stand on its own.</span>
			<div className="mg-snp-picker__footer-actions">
				<Button onClick={onClose} data-testid="snp-picker-gene-cancel">
					Cancel
				</Button>
				<Button type="primary" icon={<PlusOutlined />} disabled={geneSymbol.trim() === ""} loading={submitting} onClick={handleAddGene} data-testid="snp-picker-gene-add">
					Add gene
				</Button>
			</div>
		</div>
	);

	const importFooter = (
		<div className="mg-snp-picker__footer">
			<span className="mg-snp-picker__footer-hint">Existing rows are updated in place; new ones are created.</span>
			<div className="mg-snp-picker__footer-actions">
				<Button onClick={onClose} data-testid="snp-picker-close">
					Close
				</Button>
				<Button type="primary" icon={<UploadOutlined />} disabled={records.length === 0} loading={importing} onClick={handleImport} data-testid="snp-picker-import">
					Import {records.length} {records.length === 1 ? "row" : "rows"}
				</Button>
			</div>
		</div>
	);

	const footer = tab === "search" ? searchFooter : tab === "genes" ? geneFooter : importFooter;

	return (
		<Modal
			open
			centered
			width={MODAL_WIDTH}
			className="mg-snp-picker"
			onCancel={onClose}
			maskClosable={!submitting && !importing}
			title={
				<div className="mg-snp-picker__title">
					<span className="mg-snp-picker__title-main">Add from the registry</span>
					<span className="mg-snp-picker__title-sub">
						{panel.name_panel}
						{targetTrait ? ` › ${targetTrait}` : ""} · {panelStatusOf(panel)}
					</span>
				</div>
			}
			footer={footer}
			data-testid="snp-picker"
		>
			<div className="mg-snp-picker__body" data-testid="snp-picker-body">
				{errorMessages.length > 0 && (
					<Alert
						type="error"
						showIcon
						closable
						onClose={() => setErrorMessages([])}
						data-testid="snp-picker-error"
						message={errorMessages.length === 1 ? errorMessages[0] : "Could not complete"}
						description={errorMessages.length === 1 ? undefined : errorMessages.join(" ")}
					/>
				)}

				<Tabs
					activeKey={tab}
					onChange={setTab}
					size="small"
					items={[
						{ key: "search", label: "Search registry", children: searchTab },
						...(canPickGenes ? [{ key: "genes", label: "Add gene", children: geneTab }] : []),
						{ key: "import", label: "Bulk import", children: importTab },
					]}
				/>
			</div>
		</Modal>
	);
};

export default WheelSnpPicker;
